import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import useUser from '@/hooks/useUser';
import { queryFn, getQueryKey, QUERY_NAME } from './queryFn';
import type { CompanyScreenerResults } from '../types';
import { calcNextPage } from '../utils';

type HookParams = {
  gridViewId?: number;
  limit: number;
  initPage?: number;
  enabled?: boolean;
  displayRecentlyAddedCompanies?: boolean;
  returnRecentCompaniesOnly?: boolean;
  adjustedFilterJson: string;
};

export const useInvalidateUserPredefinedScreenersQuery = () => {
  const queryClient = useQueryClient();
  const invalidateQuery = () => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_NAME],
    });
  };
  return { invalidateQuery };
};

export const useInfiniteUserPredefinedScreenersQuery = ({
  gridViewId = 0,
  displayRecentlyAddedCompanies = true,
  returnRecentCompaniesOnly = false,
  limit,
  initPage = 0,
  adjustedFilterJson,
  enabled = true,
}: HookParams) => {
  const { data } = useUser();
  const isGuest = !data.loggedIn;

  return useInfiniteQuery({
    enabled,
    queryFn,
    queryKey: getQueryKey({
      gridViewId,
      isGuest,
      limit,
      initPage,
      displayRecentlyAddedCompanies,
      returnRecentCompaniesOnly,
      adjustedFilterJson,
    }),
    staleTime: 60000 * 60, // 1 hour
    initialPageParam: initPage,
    retry: 1,
    getNextPageParam: (
      lastPage: CompanyScreenerResults,
      allPages: CompanyScreenerResults[]
    ) => {
      const totalHits = lastPage.totalHits;
      const newCompaniesTotalHits = lastPage.newCompaniesTotalHits;
      const allCurrentPagesLength = allPages.length;
      const searchRestrictions = lastPage.meta?.searchRestrictions;

      return calcNextPage({
        limit,
        initPage,
        totalHits: returnRecentCompaniesOnly
          ? newCompaniesTotalHits
          : totalHits,
        allCurrentPagesLength,
        searchRestrictions,
      });
    },
  });
};
